import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { followService } from '../services/follow.service';
import {
  setFollowing,
  setFollowingCount,
  setFollowStatus,
  toggleFollowOptimistic,
  revertFollowOptimistic,
  setLoading,
  setActionLoading,
  setError,
  selectFollowing,
  selectFollowingCount,
  selectFollowPagination,
  selectFollowLoading,
  selectActionLoading,
  selectIsFollowing,
} from '../store/slices/followSlice';
import { useToast } from '../components/common/Toast';

export const useFollow = () => {
  const dispatch = useDispatch();
  const toast = useToast();

  const following = useSelector(selectFollowing);
  const followingCount = useSelector(selectFollowingCount);
  const pagination = useSelector(selectFollowPagination);
  const loading = useSelector(selectFollowLoading);
  const actionLoading = useSelector(selectActionLoading);

  const fetchFollowing = useCallback(async (params = {}) => {
    dispatch(setLoading(true));
    try {
      const res = await followService.getFollowing(params);
      dispatch(setFollowing(res.data));
    } catch {
      dispatch(setError('Failed to load following list'));
    }
  }, [dispatch]);

  const fetchFollowingCount = useCallback(async () => {
    try {
      const res = await followService.getFollowingCount();
      dispatch(setFollowingCount(res.data?.count ?? 0));
    } catch {
      // silently fail — non-critical
    }
  }, [dispatch]);

  const checkFollowStatus = useCallback(async (sellerId) => {
    try {
      const res = await followService.checkFollowing(sellerId);
      dispatch(setFollowStatus({ sellerId, isFollowing: res.data?.isFollowing ?? false }));
    } catch {
      dispatch(setFollowStatus({ sellerId, isFollowing: false }));
    }
  }, [dispatch]);

  const toggleFollow = useCallback(async (sellerId, currentlyFollowing) => {
    dispatch(setActionLoading(true));
    // Optimistic update
    dispatch(toggleFollowOptimistic({ sellerId }));

    try {
      if (currentlyFollowing) {
        await followService.unfollowSeller(sellerId);
        toast.success('Unfollowed seller');
      } else {
        await followService.followSeller(sellerId);
        toast.success('Now following seller');
      }
    } catch (err) {
      // Revert on failure
      dispatch(revertFollowOptimistic({ sellerId, wasFollowing: currentlyFollowing }));
      toast.error(err?.response?.data?.message || 'Action failed. Please try again.');
    } finally {
      dispatch(setActionLoading(false));
    }
  }, [dispatch, toast]);

  return {
    following,
    followingCount,
    pagination,
    loading,
    actionLoading,
    fetchFollowing,
    fetchFollowingCount,
    checkFollowStatus,
    toggleFollow,
  };
};

// Convenience hook for a single seller's follow status
export const useFollowStatus = (sellerId) => {
  const isFollowing = useSelector(selectIsFollowing(sellerId));
  return isFollowing;
};